/**
 * The one search every element table uses.
 *
 * ONE box, matching any column, over the rows the screen is holding - and the
 * screen holds all of them. That is the whole design, and both halves of it
 * matter:
 *
 *  - **Any column**, because the alternative is a per-screen list of which
 *    fields are searchable, and those lists go stale the moment the registry
 *    gains a field. `EntityTable` searched 'ID, name or qualified name' and the
 *    requirements screen searched 'ID, name or statement'; neither was written
 *    down anywhere the other could see, and a program that adds an attribute
 *    gets it in the table and not in the search.
 *  - **Over every row**, because a filter applied in the browser over a PARTIAL
 *    fetch silently searches only the part it happens to be holding, which is a
 *    wrong answer that looks exactly like a right one. The screens that call
 *    this must fetch completely. That is not this module's job to enforce, but
 *    it is the assumption this module is only correct under.
 *
 * Faceting is NOT here either - but not for the reason issue #35 gave. #35
 * removed a five-menu bar on the grounds that the grid filters by column
 * already, so a dropdown per column was the same guard written twice. That was
 * half right: the community grid holds exactly ONE column condition, and it
 * cannot answer "level 3 AND subsystem structures" (issue #68). So a few
 * client-side quick filters came back, in quickFilter.ts - applied AFTER this
 * search and BEFORE the grid's column condition, and not the server-side facets
 * #35 removed. This module stays the free-text half. The three derived shapes
 * the old `state` menu offered survive as column filters: 'unresolved' is
 * status isAnyOf tbd/tbr/tbc, 'unverified' is `verified_by` isEmpty, and
 * 'orphan' is `sources` isEmpty - and 'orphan' is a Findings check besides.
 *
 * Pure on purpose: an off-by-one in matching is invisible on screen and obvious
 * in a unit test.
 */

import { GridLogicOperator } from '@mui/x-data-grid'
import type { EntityRow } from '../../schema'

/**
 * Keys that are on every row for the app's own bookkeeping rather than for a
 * reader, and would only ever match by accident.
 *
 * `kind` is the big one: every row on the requirements screen carries
 * `kind: 'requirement'`, so without this a search for 'require' matches all of
 * them and reads as a search that is not working.
 */
const NOT_SEARCHABLE = new Set(['kind', 'line'])

/**
 * Flatten one row to the text a search should see.
 *
 * Nested objects and arrays are stringified rather than walked: a reference
 * arrives as an object with a name in it, and finding that name is the point.
 * `null` and `undefined` become empty rather than the strings 'null' and
 * 'undefined', which would otherwise both be searchable words.
 */
export function searchableText(row: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(row)) {
    if (NOT_SEARCHABLE.has(key)) continue
    if (value === null || value === undefined) continue
    parts.push(typeof value === 'object' ? JSON.stringify(value) : String(value))
  }
  return parts.join(' ').toLowerCase()
}

/**
 * Does this text contain every term of the query?
 *
 * Split out from `matchesQuery` because the tree screens build their own
 * haystack from a node's usage and definition, and were each doing their own
 * single-substring test on it. One term rule for every filter in the app, so
 * that 'tbr structures' means the same thing on the flowdown as on the table.
 *
 * `text` is expected to be lower-cased already - `searchableText` returns it
 * that way, and re-lowering a haystack per keystroke is the one wasteful thing
 * in a filter that runs over every row.
 */
export function matchesTerms(text: string, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return true
  return terms.every((term) => text.includes(term))
}

/**
 * Does this row match the query?
 *
 * Every whitespace-separated term must appear somewhere in the row, in any
 * order and in any column. 'struct tbr' finds the structures requirement that
 * is TBR without the person having to know which column holds which - which is
 * the behaviour a single box has to have to be worth replacing five menus with.
 *
 * An empty or whitespace-only query matches everything, so a cleared box is an
 * unfiltered list rather than an empty one.
 */
export function matchesQuery(row: Record<string, unknown>, query: string): boolean {
  return matchesTerms(searchableText(row), query)
}

/** The rows of `rows` that match `query`, in their original order. */
export function filterRows(rows: EntityRow[], query: string): EntityRow[] {
  if (!query.trim()) return rows
  return rows.filter((row) => matchesQuery(row, query))
}

/**
 * The query-string key the filter travels under.
 *
 * The filter lives in the URL rather than in component state so that a filtered
 * list is a LINK - somebody pastes "the structures requirements that are TBR"
 * into an issue - and so that a reload lands back on the same question rather
 * than on everything. That property predates this change and survives it; the
 * five menus that used to add `level`, `status`, `segment`, `subsystem` and
 * `state` alongside it are now column filters instead.
 */
export const SEARCH_KEY = 'search'

/** Read the filter out of a URL query string. */
export function readSearch(params: URLSearchParams): string {
  return params.get(SEARCH_KEY) ?? ''
}

/**
 * The query-string key the grid's COLUMN filters travel under.
 *
 * Separate from `search` because they are separate controls: the box is one
 * question over every column, and the column filters are per-column
 * conditions. Both belong in the URL for the same reason - a filtered table is
 * a link - and this is the half that would otherwise have been lost when the
 * five dropdowns became column filters. The dropdowns were linkable; nothing
 * would have said the column filters were not.
 */
export const FILTER_KEY = 'filter'

/**
 * One column condition, as it travels in the URL.
 *
 * A deliberate subset of MUI's `GridFilterItem`: field, operator and value are
 * the question, and `id` is a per-session handle the grid assigns, which has no
 * business in a link somebody pastes into an issue.
 */
export interface ColumnFilter {
  field: string
  operator: string
  value?: unknown
}

/**
 * The column filters as they travel: a list, plus how they combine.
 *
 * `logicOperator` is MUI's own enum rather than a `'and' | 'or'` union of our
 * own. It is a string enum, so it survives a JSON round trip unchanged - and
 * taking the type from the grid means the URL model and the grid model cannot
 * drift into disagreeing about a value one of them accepts.
 */
export interface ColumnFilterModel {
  items: ColumnFilter[]
  logicOperator?: GridLogicOperator
}

/** Nothing filtered. Its own constant so callers cannot disagree about it. */
const NO_FILTERS: ColumnFilterModel = { items: [] }

/**
 * Read the column filters out of a URL query string.
 *
 * **Defensive, in one direction only.** A query string is hand-editable and
 * survives a redeploy, so anything that is not a well-formed model is dropped
 * rather than handed to the grid: MUI throws on a malformed `filterModel`, and
 * a crash on load is a far worse answer to a stale link than an unfiltered
 * table. Same reasoning the old `filters.ts` applied to a hand-edited `level`.
 *
 * Being strict about the SHAPE rather than the CONTENTS is deliberate. An
 * operator the grid does not know, or a field that no longer exists, is left
 * alone here: the grid ignores an item it cannot apply, and dropping those
 * would mean this function needed to know the column set.
 */
export function readFilterModel(params: URLSearchParams): ColumnFilterModel {
  const raw = params.get(FILTER_KEY)
  if (!raw) return NO_FILTERS

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return NO_FILTERS

    const { items, logicOperator } = parsed as Partial<ColumnFilterModel>
    if (!Array.isArray(items)) return NO_FILTERS

    const kept = items.filter(
      (item): item is ColumnFilter =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as ColumnFilter).field === 'string' &&
        typeof (item as ColumnFilter).operator === 'string',
    )
    if (kept.length === 0) return NO_FILTERS

    return logicOperator === GridLogicOperator.Or
      ? { items: kept, logicOperator }
      : { items: kept }
  } catch {
    return NO_FILTERS
  }
}

/**
 * Write the column filters back into a query string.
 *
 * An item the person has started but not finished - a column and an operator
 * chosen, no value typed yet - is NOT written. The grid reports one on every
 * keystroke of building a filter, and putting it in the URL would replace the
 * history entry with a half-question and make the link mean something the
 * table never showed. `isEmpty` and `isNotEmpty` are the exception: they take
 * no value, and are complete without one.
 */
const VALUELESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty'])

export function writeFilterModel(
  params: URLSearchParams,
  model: ColumnFilterModel,
): URLSearchParams {
  const next = new URLSearchParams(params)

  const ready = (model.items ?? []).filter(
    (item) =>
      VALUELESS_OPERATORS.has(item.operator) ||
      (item.value !== undefined && item.value !== null && item.value !== ''),
  )

  if (ready.length === 0) {
    next.delete(FILTER_KEY)
    return next
  }

  // Only the three fields that are the question - see ColumnFilter. Rebuilt
  // rather than passed through, so a future MUI field cannot leak into a link.
  const payload: ColumnFilterModel = {
    items: ready.map(({ field, operator, value }) =>
      value === undefined ? { field, operator } : { field, operator, value },
    ),
  }
  if (model.logicOperator === GridLogicOperator.Or) payload.logicOperator = GridLogicOperator.Or

  next.set(FILTER_KEY, JSON.stringify(payload))
  return next
}

/**
 * The query-string key the grid's SORT travels under (issue #86).
 *
 * In the URL for the reason the filters are: what order a list is in is part of
 * what somebody was looking at, and it is what came back wrong after opening a
 * requirement and returning to the list. Kept beside the filters so that it is
 * remembered with them (see rememberedFilters).
 */
export const SORT_KEY = 'sort'

/** One column's sort, as it travels: MUI's own `GridSortItem`, minus nothing. */
export interface ColumnSort {
  field: string
  sort: 'asc' | 'desc'
}

/**
 * Read the sort out of a URL query string.
 *
 * Defensive for the reason `readFilterModel` is - a hand-edited or month-old
 * link must not crash the grid - so anything that is not a list of
 * `{field, sort: 'asc' | 'desc'}` is dropped. A field that no longer exists is
 * left for the grid to ignore.
 */
export function readSortModel(params: URLSearchParams): ColumnSort[] {
  const raw = params.get(SORT_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (item): item is ColumnSort =>
          typeof item === 'object' &&
          item !== null &&
          typeof (item as ColumnSort).field === 'string' &&
          ((item as ColumnSort).sort === 'asc' || (item as ColumnSort).sort === 'desc'),
      )
      .map(({ field, sort }) => ({ field, sort }))
  } catch {
    return []
  }
}

/**
 * Write the sort back into a query string.
 *
 * A cleared sort (MUI reports `sort: null` on the third click, or an empty
 * model) removes the key, so an unsorted list is the bare path.
 */
export function writeSortModel(
  params: URLSearchParams,
  model: readonly { field: string; sort?: string | null }[],
): URLSearchParams {
  const next = new URLSearchParams(params)
  const kept = model
    .filter((item) => item.sort === 'asc' || item.sort === 'desc')
    .map(({ field, sort }) => ({ field, sort }))
  if (kept.length === 0) next.delete(SORT_KEY)
  else next.set(SORT_KEY, JSON.stringify(kept))
  return next
}

/**
 * Write the filter back into a query string.
 *
 * An empty filter is omitted rather than written as `search=`, so the URL of an
 * unfiltered list is the bare path and the link somebody shares says what they
 * mean.
 *
 * Other parameters already on the URL are preserved: this owns one key, not the
 * whole query string, and a screen that grows a second piece of state should not
 * have it silently dropped by a keystroke.
 */
export function writeSearch(params: URLSearchParams, query: string): URLSearchParams {
  const next = new URLSearchParams(params)
  if (query) next.set(SEARCH_KEY, query)
  else next.delete(SEARCH_KEY)
  return next
}
